import React, { useState, useMemo } from 'react';
import { Award, Calculator, TrendingUp, BookOpen } from 'lucide-react';
import { Card, Input } from '../ui';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b'];

export function CertificationPricingCalculator() {
  const [lmsOverhead, setLmsOverhead] = useState<number>(12000); // Fixed annual LMS cost
  const [instructorFees, setInstructorFees] = useState<number>(5000); // Fixed content creation / instructor stipends
  const [proctoringFee, setProctoringFee] = useState<number>(35); // Variable cost per student
  const [materialsCost, setMaterialsCost] = useState<number>(15); // Variable cost per student (e.g. digital badges, study guides)
  const [targetVolume, setTargetVolume] = useState<number>(200); // Expected number of test takers
  const [desiredMargin, setDesiredMargin] = useState<number>(30); // Target profit margin %

  const model = useMemo(() => {
    const fixedCosts = lmsOverhead + instructorFees;
    const fixedCostPerStudent = targetVolume > 0 ? fixedCosts / targetVolume : 0;
    
    const variableCostPerStudent = proctoringFee + materialsCost;
    
    const totalCostPerStudent = fixedCostPerStudent + variableCostPerStudent;
    
    // Price = Cost / (1 - margin)
    const suggestedPrice = desiredMargin >= 100 
      ? totalCostPerStudent * 2 
      : totalCostPerStudent / (1 - (desiredMargin / 100));
      
    const profitPerStudent = suggestedPrice - totalCostPerStudent;
    const totalProjectedProfit = profitPerStudent * targetVolume;
    const totalRevenue = suggestedPrice * targetVolume;

    const chartData = [
      { name: 'Fixed Overhead', value: fixedCostPerStudent, color: COLORS[0] },
      { name: 'Test Proctoring', value: proctoringFee, color: COLORS[1] },
      { name: 'Study Materials', value: materialsCost, color: COLORS[2] },
      { name: 'Net Profit', value: profitPerStudent, color: COLORS[3] }
    ].filter(d => d.value > 0);

    return {
      totalCostPerStudent,
      suggestedPrice,
      profitPerStudent,
      totalProjectedProfit,
      totalRevenue,
      chartData
    };
  }, [lmsOverhead, instructorFees, proctoringFee, materialsCost, targetVolume, desiredMargin]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-blue-800 to-indigo-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Award className="w-6 h-6 mr-3 text-blue-300" />
            Certification Pricing Calculator
          </h2>
          <p className="text-blue-100 max-w-xl">
            Model the economics of your trade association's credentialing program. Calculate the suggested retail price per test-taker to cover platform overhead and hit your revenue goals.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        
        {/* Controls */}
        <div className="md:col-span-1 space-y-6">
          <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-2">Program Fixed Costs</h3>
            
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-2">Annual LMS Platform Fee ($)</label>
              <Input 
                type="number" 
                value={lmsOverhead} 
                onChange={(e) => setLmsOverhead(Number(e.target.value))}
              />
            </div>
            
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-2">Instructor / Content Fees ($)</label>
              <Input 
                type="number" 
                value={instructorFees} 
                onChange={(e) => setInstructorFees(Number(e.target.value))}
              />
            </div>

            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-2 pt-4">Variable Costs (Per Student)</h3>
            
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-2">Test Proctoring Fee ($)</label>
              <Input 
                type="number" 
                value={proctoringFee} 
                onChange={(e) => setProctoringFee(Number(e.target.value))}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-2">Badges & Materials ($)</label>
              <Input 
                type="number" 
                value={materialsCost} 
                onChange={(e) => setMaterialsCost(Number(e.target.value))}
              />
            </div>

            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-2 pt-4">Revenue Targets</h3>

            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase flex items-center mb-2">
                <BookOpen className="w-4 h-4 mr-2 text-indigo-500" /> Target Annual Enrollment
              </label>
              <Input 
                type="number" 
                value={targetVolume} 
                onChange={(e) => setTargetVolume(Number(e.target.value))}
                className="font-bold border-indigo-200 focus-visible:ring-indigo-500"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase flex justify-between mb-2">
                <span>Target Profit Margin</span>
                <span className="font-bold text-slate-900 dark:text-white">{desiredMargin}%</span>
              </label>
              <input 
                type="range" 
                min="0" 
                max="80" 
                value={desiredMargin} 
                onChange={(e) => setDesiredMargin(Number(e.target.value))}
                className="w-full accent-indigo-600"
              />
            </div>

          </Card>
        </div>

        {/* Results */}
        <div className="md:col-span-2 space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-slate-900 text-white flex flex-col justify-center">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center">
                <Calculator className="w-4 h-4 mr-2" /> Suggested Retail Price
              </h3>
              <div className="text-5xl font-black text-white tracking-tight mb-2">
                {formatCurrency(model.suggestedPrice)}
              </div>
              <p className="text-sm text-slate-400">
                Break-even cost per student is <strong>{formatCurrency(model.totalCostPerStudent)}</strong>
              </p>
            </Card>

            <Card className="p-6 border-emerald-200 dark:border-emerald-900 shadow-xl bg-emerald-50 dark:bg-emerald-900/10 flex flex-col justify-center">
              <h3 className="text-sm font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider mb-2 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" /> Projected Net Profit
              </h3>
              <div className="text-4xl font-black text-emerald-600 dark:text-emerald-400 mb-2">
                {formatCurrency(model.totalProjectedProfit)}
              </div>
              <p className="text-sm text-emerald-800 dark:text-emerald-300">
                Based on {targetVolume} expected enrollments
              </p>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-lg font-bold mb-6">Per-Student Revenue Breakdown</h3>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="h-[250px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={model.chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                    >
                      {model.chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => formatCurrency(Number(value))}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-4">
                {model.chartData.map((item) => (
                  <div key={item.name} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="font-bold text-sm" style={{ color: item.color }}>{item.name}</div>
                    <div className="font-bold">{formatCurrency(item.value)}</div>
                  </div>
                ))}
                <div className="flex justify-between items-center p-3 rounded-lg bg-slate-100 dark:bg-slate-800 mt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="font-bold text-sm text-slate-500 uppercase">Suggested Price</div>
                  <div className="font-bold text-slate-900 dark:text-white">{formatCurrency(model.suggestedPrice)}</div>
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
